<template>
    <div class="ds-wrap h-full w-full flex flex-col text-left">
        <div class="ds-toolbar">
            <div class="ds-toolbar__row">
                <div class="ds-filters">
                    <div class="ds-filter">
                        <label class="ds-filter__label">Office Type</label>
                        <select v-model="filters.office_type" class="ds-select" @change="onFilterChange">
                            <option v-for="opt in officeTypeFilterOptions" :key="'office-type-filter-' + opt.value"
                                :value="opt.value">
                                {{ opt.label }}
                            </option>
                        </select>
                    </div>
                    <div class="ds-filter">
                        <label class="ds-filter__label">Floor</label>
                        <input v-model="filters.floor" list="academic-office-floor-filter-options" type="text"
                            class="ds-input ds-filter-input" placeholder="All" @input="onFilterChange" />
                        <datalist id="academic-office-floor-filter-options">
                            <option v-for="opt in floorFilterOptions" :key="'floor-filter-' + opt" :value="opt">
                                {{ opt }}
                            </option>
                        </datalist>
                    </div>
                    <div class="ds-filter">
                        <label class="ds-filter__label">Room Type</label>
                        <select v-model="filters.room_type" class="ds-select" @change="onFilterChange">
                            <option v-for="opt in roomTypeFilterOptions" :key="'room-type-filter-' + opt.value"
                                :value="opt.value">
                                {{ opt.label }}
                            </option>
                        </select>
                    </div>
                    <div class="ds-filter">
                        <label class="ds-filter__label">Occupied</label>
                        <select v-model="filters.is_occupied" class="ds-select" @change="onFilterChange">
                            <option v-for="opt in occupiedFilterOptions" :key="'occupied-filter-' + opt.value"
                                :value="opt.value">
                                {{ opt.label }}
                            </option>
                        </select>
                    </div>
                    <div class="ds-search">
                        <search ref="searchRef" @on-search="onSearch" class-list="px-3 h-9"></search>
                    </div>
                </div>

                <div class="ds-toolbar__actions">
                    <button type="button" class="ds-export-btn" :disabled="isExportExcel" @click="generateReport">
                        <font-awesome-icon :icon="['fas', 'file-excel']" class="ds-export-btn__icon" />
                        <span>{{ isExportExcel ? "Exporting..." : "Export Excel" }}</span>
                    </button>
                    <button v-if="canDelete" type="button" class="ds-btn ds-btn--danger"
                        :disabled="deletingSelected || selectedIds.length === 0" @click="confirmBulkDelete">
                        <font-awesome-icon icon="trash" class="mr-2" />
                        <span>{{ deletingSelected ? "Deleting..." : "Delete Selected" }}</span>
                    </button>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--ghost" :disabled="bulkUploading"
                        @click="downloadBulkTemplate">
                        <font-awesome-icon :icon="['fas', 'download']" class="mr-2" />
                        <span>Download Template</span>
                    </button>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" :disabled="bulkUploading"
                        @click="openBulkUploadModal">
                        <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" class="mr-2" />
                        <span>{{ bulkUploading ? "Uploading..." : "Bulk Upload" }}</span>
                    </button>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" @click="openCreate">
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                        Add office
                    </button>
                </div>

                <input ref="bulkFileInput" type="file"
                    accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    style="display: none" @change="onBulkFileSelected" />
            </div>
        </div>

        <div class="flex-auto flex flex-col px-4 sm:px-8 pb-8">
            <div v-if="loading" class="flex-auto flex flex-col">
                <table-shimmer :titleHeaders="tableHeaders" :rows="perPage" />
            </div>

            <div v-else-if="!loading && items.length === 0" class="flex-auto flex justify-center items-center py-16">
                <div class="text-center max-w-md px-4">
                    <div class="ds-empty-icon">🗂️</div>
                    <h2 class="text-xl font-semibold text-gray-800 mb-2">No academic offices found</h2>
                    <p class="text-gray-500 mb-4">
                        Try adjusting filters, or add a new office.
                    </p>
                    <button v-if="canCreate" type="button" class="ds-btn ds-btn--primary" @click="openCreate">
                        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
                        Add office
                    </button>
                </div>
            </div>

            <template v-else>
                <div class="ds-table-shell overflow-x-auto">
                    <table class="min-w-full text-left text-sm">
                        <thead>
                            <tr>
                                <th class="ds-th w-12">
                                    <input type="checkbox" class="ds-checkbox" :disabled="!canDelete"
                                        :checked="allVisibleSelected" @change="toggleSelectAll" />
                                </th>
                                <th class="ds-th w-12">#</th>
                                <th class="ds-th">Office Type</th>
                                <th class="ds-th">Room</th>
                                <th class="ds-th">Floor</th>
                                <th class="ds-th">Occupied</th>
                                <th class="ds-th">Stations</th>
                                <th class="ds-th">Room Type</th>
                                <th class="ds-th">Toilet</th>
                                <th class="ds-th">Meeting</th>
                                <th class="ds-th">Waiting</th>
                                <th class="ds-th">Assistance</th>
                                <th v-if="canManage" class="ds-th text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in items" :key="item.id" class="ds-row">
                                <td class="ds-td w-12">
                                    <input type="checkbox" class="ds-checkbox" :disabled="!canDelete"
                                        :value="String(item.id)" v-model="selectedIds" @click.stop />
                                </td>
                                <td class="ds-td text-gray-400 font-medium">{{ rowNumber(index) }}</td>
                                <td class="ds-td">{{ item.office_type || "—" }}</td>
                                <td class="ds-td font-medium">{{ item.room || "—" }}</td>
                                <td class="ds-td">{{ item.floor || "—" }}</td>
                                <td class="ds-td">{{ formatBooleanDisplay(item.is_occupied) }}</td>
                                <td class="ds-td">{{ formatStationsCount(item.stations_count) }}</td>
                                <td class="ds-td">{{ item.room_type || "—" }}</td>
                                <td class="ds-td">{{ formatBooleanDisplay(item.has_toilet) }}</td>
                                <td class="ds-td">{{ formatBooleanDisplay(item.has_meeting) }}</td>
                                <td class="ds-td">{{ formatBooleanDisplay(item.has_waiting) }}</td>
                                <td class="ds-td">{{ formatBooleanDisplay(item.has_assistance_office) }}</td>
                                <td v-if="canManage" class="ds-td text-right whitespace-nowrap">
                                    <button v-if="canUpdate" type="button" title="Edit" class="ds-icon-btn"
                                        @click="openEdit(item)">
                                        <font-awesome-icon icon="pen" class="w-3 h-3 text-gray-700" />
                                    </button>
                                    <button v-if="canDelete" type="button" title="Delete"
                                        class="ds-icon-btn ds-icon-btn--danger" @click="confirmDelete(item)">
                                        <font-awesome-icon icon="trash" class="w-3 h-3 text-gray-700" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="mt-4" v-if="paginatorInfoWithItems && paginatorInfoWithItems.total">
                    <pagination :paginatorInfo="paginatorInfoWithItems" :setPage="setPage"></pagination>
                </div>
            </template>
        </div>

        <transition name="fade">
            <div v-if="showFormModal"
                class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
                @click.self="closeForm">
                <div class="ds-form bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
                    <div class="ds-form__header">
                        <h3 class="text-lg font-semibold text-gray-900">
                            {{ editingId ? "Edit office" : "Add office" }}
                        </h3>
                        <p class="text-sm text-gray-500 mt-1">
                            {{ entity }} · {{ campus }}
                        </p>
                    </div>

                    <div class="ds-form__body grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label class="ds-label">Office Type <span class="text-red-500">*</span></label>
                            <select v-model="form.office_type" class="ds-input">
                                <option value="">Select office type</option>
                                <option v-for="opt in officeTypeOptions" :key="'office-type-form-' + opt" :value="opt">
                                    {{ opt }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Room <span class="text-red-500">*</span></label>
                            <input v-model="form.room" type="text" class="ds-input"
                                placeholder="Room number (e.g. C-106)" />
                        </div>
                        <div>
                            <label class="ds-label">Floor</label>
                            <input v-model="form.floor" list="academic-office-floor-form-options" type="text"
                                class="ds-input" placeholder="Floor" />
                            <datalist id="academic-office-floor-form-options">
                                <option v-for="opt in floorFilterOptions" :key="'floor-form-' + opt" :value="opt">
                                    {{ opt }}
                                </option>
                            </datalist>
                        </div>
                        <div>
                            <label class="ds-label">Occupied</label>
                            <select v-model="form.is_occupied" class="ds-input">
                                <option v-for="opt in yesNoBlankOptions" :key="'occupied-form-' + opt.value"
                                    :value="opt.value">
                                    {{ opt.label }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Stations</label>
                            <input v-model="form.stations_count" type="number" min="0" class="ds-input"
                                placeholder="Number of stations" />
                        </div>
                        <div>
                            <label class="ds-label">Room Type</label>
                            <select v-model="form.room_type" class="ds-input">
                                <option value="">Select room type</option>
                                <option v-for="opt in roomTypeOptions" :key="'room-type-form-' + opt" :value="opt">
                                    {{ opt }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Toilet</label>
                            <select v-model="form.has_toilet" class="ds-input">
                                <option v-for="opt in yesNoOptions" :key="'toilet-form-' + opt.value"
                                    :value="opt.value">
                                    {{ opt.label }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Meeting</label>
                            <select v-model="form.has_meeting" class="ds-input">
                                <option v-for="opt in yesNoOptions" :key="'meeting-form-' + opt.value"
                                    :value="opt.value">
                                    {{ opt.label }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Waiting</label>
                            <select v-model="form.has_waiting" class="ds-input">
                                <option v-for="opt in yesNoOptions" :key="'waiting-form-' + opt.value"
                                    :value="opt.value">
                                    {{ opt.label }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label class="ds-label">Assistance Office</label>
                            <select v-model="form.has_assistance_office" class="ds-input">
                                <option v-for="opt in yesNoOptions" :key="'assistance-form-' + opt.value"
                                    :value="opt.value">
                                    {{ opt.label }}
                                </option>
                            </select>
                        </div>
                        <div class="sm:col-span-2">
                            <label class="ds-label">Remarks</label>
                            <textarea v-model="form.remarks" class="ds-input ds-textarea" rows="3"
                                placeholder="Optional remarks"></textarea>
                        </div>
                    </div>

                    <div class="ds-form__footer">
                        <button type="button" class="ds-btn ds-btn--ghost" @click="closeForm">
                            Cancel
                        </button>
                        <base-button type="button" :disabled="saving" class="bg-teal-700 hover:bg-teal-800 text-white"
                            @click="saveForm">
                            {{ saving ? "Saving…" : "Save" }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>

        <transition name="fade">
            <base-modal v-if="showBulkUploadModal" :title="'Bulk Upload Academic Offices'"
                @close="closeBulkUploadModal">
                <div class="ds-bulk">
                    <div class="ds-bulk__banner">
                        <div class="ds-bulk__banner-icon">
                            <font-awesome-icon :icon="['fas', 'file-excel']" />
                        </div>
                        <div class="ds-bulk__banner-body">
                            <p class="ds-bulk__banner-title">Upload Academic Offices</p>
                            <p class="ds-bulk__banner-hint">
                                Entity and campus are automatic. Use the template and keep column headers unchanged.
                            </p>
                        </div>
                    </div>

                    <div v-if="bulkStep === 'select'" class="ds-bulk__section">
                        <div class="ds-bulk__dropzone" :class="{ 'ds-bulk__dropzone--active': bulkDragOver }"
                            @click="triggerBulkFilePicker" @dragover.prevent="bulkDragOver = true"
                            @dragleave.prevent="bulkDragOver = false" @drop.prevent="onBulkFileDrop">
                            <font-awesome-icon :icon="['fas', 'cloud-upload-alt']" class="ds-bulk__dropzone-icon" />
                            <p class="ds-bulk__dropzone-title">Drop your Excel file here, or click to browse</p>
                            <p class="ds-bulk__dropzone-sub">Accepted formats: .xlsx, .xls</p>
                        </div>

                        <div class="ds-bulk__tips">
                            <button type="button" class="ds-bulk__link-btn" :disabled="bulkUploading"
                                @click.stop="downloadBulkTemplate">
                                <font-awesome-icon :icon="['fas', 'download']" class="mr-2" />
                                Download Excel template
                            </button>
                            <p class="ds-bulk__tips-text">
                                Required column: <strong>Room</strong>. Room is required for each row.
                                Optional columns: <strong>Office Type</strong>, <strong>Floor</strong>,
                                <strong>Occupied</strong>, <strong>Stations</strong>, <strong>Room Type</strong>,
                                <strong>Toilet</strong>, <strong>Meeting</strong>, <strong>Waiting</strong>,
                                <strong>Assistance Office</strong>, and <strong>Remarks</strong>.
                                Use Yes/No for occupied and amenity columns.
                            </p>
                        </div>
                    </div>

                    <div v-else-if="bulkStep === 'review'" class="ds-bulk__section">
                        <div class="ds-bulk__file-card">
                            <div class="ds-bulk__file-icon">
                                <font-awesome-icon :icon="['fas', 'file-excel']" />
                            </div>
                            <div class="min-w-0 flex-1">
                                <p class="ds-bulk__file-name">{{ bulkFileName }}</p>
                                <p class="ds-bulk__file-meta">
                                    {{ bulkValidRows.length }} valid row{{ bulkValidRows.length === 1 ? "" : "s" }}
                                    <span v-if="bulkValidationErrors.length">
                                        · {{ bulkValidationErrors.length }} issue{{ bulkValidationErrors.length === 1 ?
                                            "" : "s"
                                        }}
                                    </span>
                                </p>
                            </div>
                        </div>

                        <div v-if="bulkValidationErrors.length" class="ds-bulk__errors">
                            <div class="ds-bulk__errors-head">
                                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-2" />
                                Fix the following before upload
                            </div>
                            <div class="ds-bulk__errors-list">
                                <div v-for="(err, idx) in bulkValidationErrors" :key="'bulk-err-' + idx"
                                    class="ds-bulk__error-item">
                                    <span v-if="err.row" class="ds-bulk__error-row">Row {{ err.row }}</span>
                                    <span>{{ err.message }}</span>
                                </div>
                            </div>
                        </div>

                        <div v-else class="ds-bulk__ready">
                            <font-awesome-icon :icon="['fas', 'check-circle']" class="ds-bulk__ready-icon" />
                            <div>
                                <p class="ds-bulk__ready-title">Ready to upload {{ bulkValidRows.length }} record{{
                                    bulkValidRows.length === 1 ? "" : "s" }}</p>
                                <p class="ds-bulk__ready-sub">We will create rows for every valid entry.</p>
                            </div>
                        </div>

                        <div class="ds-bulk__footer">
                            <button type="button" class="ds-btn ds-btn--ghost" :disabled="bulkUploading"
                                @click="resetBulkSelection">
                                Change file
                            </button>
                            <button type="button" class="ds-btn ds-btn--primary"
                                :disabled="bulkUploading || !bulkValidRows.length || bulkValidationErrors.length"
                                @click="startBulkUpload">
                                Start Upload
                            </button>
                        </div>
                    </div>

                    <div v-else-if="bulkStep === 'uploading'" class="ds-bulk__section">
                        <div class="ds-bulk__progress-card">
                            <div class="ds-bulk__progress-head">
                                <p class="ds-bulk__progress-title">Uploading...</p>
                                <p class="ds-bulk__progress-percent">{{ bulkProgressPercent }}%</p>
                            </div>
                            <div class="ds-bulk__progress-track">
                                <div class="ds-bulk__progress-fill" :style="{ width: bulkProgressPercent + '%' }"></div>
                            </div>
                            <p class="ds-bulk__progress-sub">
                                {{ bulkProgressCurrent }} / {{ bulkProgressTotal }} · {{ bulkProgressPercent }}%
                            </p>
                            <p v-if="bulkCurrentName" class="ds-bulk__progress-name">{{ bulkCurrentName }}</p>
                        </div>
                    </div>

                    <div v-else-if="bulkStep === 'done'" class="ds-bulk__section">
                        <div class="ds-bulk__summary">
                            <div class="ds-bulk__summary-stat ds-bulk__summary-stat--ok">
                                <p class="ds-bulk__summary-value">{{ bulkSuccessCount }}</p>
                                <p class="ds-bulk__summary-label">Successful</p>
                            </div>
                            <div class="ds-bulk__summary-stat ds-bulk__summary-stat--fail">
                                <p class="ds-bulk__summary-value">{{ bulkFailCount }}</p>
                                <p class="ds-bulk__summary-label">Failed</p>
                            </div>
                            <div class="ds-bulk__summary-stat">
                                <p class="ds-bulk__summary-value">{{ bulkProgressTotal }}</p>
                                <p class="ds-bulk__summary-label">Total</p>
                            </div>
                        </div>

                        <div v-if="bulkUploadErrors.length" class="ds-bulk__errors">
                            <div class="ds-bulk__errors-head">
                                <font-awesome-icon :icon="['fas', 'times-circle']" class="mr-2" />
                                Upload errors
                            </div>
                            <div class="ds-bulk__errors-list">
                                <div v-for="(err, idx) in bulkUploadErrors" :key="'bulk-up-err-' + idx"
                                    class="ds-bulk__error-item">
                                    <span v-if="err.row" class="ds-bulk__error-row">Row {{ err.row }}</span>
                                    <span>{{ err.message }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="ds-bulk__footer ds-bulk__footer--single">
                            <button type="button" class="ds-btn ds-btn--primary" @click="closeBulkUploadModal">
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            </base-modal>
        </transition>
    </div>
</template>

<script>
import gql from "graphql-tag";
import xlsx from "xlsx/dist/xlsx.full.min.js";
import Pagination from "../../../../components/shared/pagination.vue";
import TableShimmer from "../../../../components/shared/TableShimmer.vue";
import Search from "../../../../components/shared/search.vue";

const LIST_QUERY = gql`
    query FacilityAcademicOffices(
        $first: Int!
        $page: Int!
        $entity: String
        $campus: String
        $office_type: String
        $floor: String
        $room_type: String
        $is_occupied: Boolean
        $search: String
    ) {
        facilityAcademicOffices(
            first: $first
            page: $page
            entity: $entity
            campus: $campus
            office_type: $office_type
            floor: $floor
            room_type: $room_type
            is_occupied: $is_occupied
            search: $search
        ) {
            error
            error_code
            message
            data {
                id
                entity
                campus
                office_type
                room
                floor
                is_occupied
                stations_count
                room_type
                has_toilet
                has_meeting
                has_waiting
                has_assistance_office
                remarks
                created_at
                updated_at
            }
            paginatorInfo {
                currentPage
                lastPage
                perPage
                total
                hasMorePages
                firstItem
                lastItem
            }
        }
    }
`;

const CREATE_MUTATION = gql`
    mutation CreateFacilityAcademicOffice($input: FacilityAcademicOfficeInput!) {
        createFacilityAcademicOffice(input: $input) {
            error
            error_code
            message
        }
    }
`;

const CREATE_BULK_MUTATION = gql`
    mutation CreateFacilityAcademicOfficesBulk($input: CreateFacilityAcademicOfficesBulkInput!) {
        createFacilityAcademicOfficesBulk(input: $input) {
            error
            error_code
            message
            data {
                success_count
                fail_count
                total
                results {
                    row
                    room
                    error
                    message
                }
            }
        }
    }
`;

const UPDATE_MUTATION = gql`
    mutation UpdateFacilityAcademicOffice($id: ID!, $input: FacilityAcademicOfficeInput!) {
        updateFacilityAcademicOffice(id: $id, input: $input) {
            error
            error_code
            message
        }
    }
`;

const DELETE_MUTATION = gql`
    mutation DeleteFacilityAcademicOffice($id: ID!) {
        deleteFacilityAcademicOffice(id: $id) {
            error
            error_code
            message
        }
    }
`;

const DELETE_BULK_MUTATION = gql`
    mutation DeleteFacilityAcademicOfficesBulk($input: DeleteFacilityAcademicOfficesBulkInput!) {
        deleteFacilityAcademicOfficesBulk(input: $input) {
            error
            error_code
            message
            data {
                success_count
                fail_count
                total
                results {
                    id
                    error
                    message
                }
            }
        }
    }
`;

const IT_BULK_TEMPLATE_FILENAME = "adpoly-facility-academic-offices-bulk-upload-template.xlsx";
const IT_BULK_TEMPLATE_URL = require("../../../../assets/templates/adpoly-facility-academic-offices-bulk-upload-template.xlsx");

const FLOOR_FILTER_OPTIONS = ["First Floor"];

const OFFICE_TYPE_FILTER_OPTIONS = [
    { value: "", label: "All" },
    { value: "Academic", label: "Academic" },
    { value: "Lab Engineers", label: "Lab Engineers" },
];

const ROOM_TYPE_FILTER_OPTIONS = [
    { value: "", label: "All" },
    { value: "Shared Office", label: "Shared Office" },
    { value: "Single Office", label: "Single Office" },
];

const OCCUPIED_FILTER_OPTIONS = [
    { value: "", label: "All" },
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
];

const OFFICE_TYPE_OPTIONS = ["Academic", "Lab Engineers"];
const ROOM_TYPE_OPTIONS = ["Shared Office", "Single Office"];

const YES_NO_BLANK_OPTIONS = [
    { value: "", label: "—" },
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
];

const YES_NO_OPTIONS = [
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" },
];

function emptyForm() {
    return {
        office_type: "",
        room: "",
        floor: "",
        is_occupied: "",
        stations_count: "",
        room_type: "",
        has_toilet: "no",
        has_meeting: "no",
        has_waiting: "no",
        has_assistance_office: "no",
        remarks: "",
    };
}

function normalizeHeader(value) {
    if (value === null || value === undefined) return "";
    return String(value)
        .replace(/\u00a0/g, " ")
        .trim()
        .replace(/\s+/g, " ")
        .toUpperCase();
}

function cellToString(value) {
    if (value === null || value === undefined) return "";
    if (typeof value === "number") {
        if (Math.floor(value) === value) return String(Math.trunc(value));
        return String(value);
    }
    return String(value).replace(/\u00a0/g, " ").trim();
}

function normalizeDashAsEmpty(value) {
    var v = cellToString(value);
    if (v === "-" || v.toLowerCase() === "none") return "";
    return v;
}

function resolveColumnIndex(headerIndex, canonical, alternates) {
    if (headerIndex[canonical] !== undefined) {
        return headerIndex[canonical];
    }
    if (alternates && alternates.length) {
        for (var a = 0; a < alternates.length; a++) {
            if (headerIndex[alternates[a]] !== undefined) {
                return headerIndex[alternates[a]];
            }
        }
    }
    return undefined;
}

function cellAt(row, index) {
    if (index === undefined) return "";
    return normalizeDashAsEmpty(row[index]);
}

function parseStationsCountValue(value) {
    if (value === null || value === undefined) {
        return { value: null, error: null };
    }

    if (typeof value === "number") {
        if (value < 0 || Math.floor(value) !== value) {
            return { value: null, error: "Stations must be a whole number greater than or equal to 0." };
        }
        return { value: Math.trunc(value), error: null };
    }

    var v = cellToString(value);
    if (v === "") {
        return { value: null, error: null };
    }

    if (!/^\d+$/.test(v)) {
        return { value: null, error: "Stations must be a whole number greater than or equal to 0." };
    }

    return { value: parseInt(v, 10), error: null };
}

function parseYesNoValue(value, fieldLabel) {
    if (value === null || value === undefined) {
        return { value: null, error: null };
    }

    if (typeof value === "boolean") {
        return { value: value, error: null };
    }

    var v = cellToString(value);
    if (v === "") {
        return { value: null, error: null };
    }

    var upper = v.toUpperCase();
    if (upper === "YES" || upper === "Y" || upper === "TRUE" || upper === "1") {
        return { value: true, error: null };
    }
    if (upper === "NO" || upper === "N" || upper === "FALSE" || upper === "0") {
        return { value: false, error: null };
    }

    return {
        value: null,
        error: (fieldLabel || "Field") + " must be Yes or No.",
    };
}

function formYesNoToBool(value) {
    if (value === "yes") return true;
    if (value === "no") return false;
    return null;
}

function boolToFormYesNo(value) {
    if (value === true) return "yes";
    if (value === false) return "no";
    return "";
}

function boolToFormYesNoRequired(value) {
    if (value === true) return "yes";
    if (value === false) return "no";
    return "no";
}

import exportManagerMixin from "./exportManagerMixin.js";

export default {
    name: "AcademicOfficesModule",
    mixins: [exportManagerMixin],
    components: {
        Pagination,
        TableShimmer,
        Search,
    },
    props: {
        entity: { type: String, required: true },
        campus: { type: String, required: true },
    },
    emits: ["changed"],
    data() {
        return {
            items: [],
            paginatorInfo: null,
            loading: false,
            isExportExcel: false,
            selectedIds: [],
            deletingSelected: false,
            generatedReport: [],
            showBulkUploadModal: false,
            bulkStep: "select",
            bulkDragOver: false,
            bulkFileName: "",
            bulkValidRows: [],
            bulkValidationErrors: [],
            bulkUploading: false,
            bulkProgressCurrent: 0,
            bulkProgressTotal: 0,
            bulkCurrentName: "",
            bulkSuccessCount: 0,
            bulkFailCount: 0,
            bulkUploadErrors: [],
            perPage: 25,
            page: 1,
            search: "",
            filters: {
                office_type: "",
                floor: "",
                room_type: "",
                is_occupied: "",
            },
            floorFilterOptions: FLOOR_FILTER_OPTIONS,
            officeTypeFilterOptions: OFFICE_TYPE_FILTER_OPTIONS,
            roomTypeFilterOptions: ROOM_TYPE_FILTER_OPTIONS,
            occupiedFilterOptions: OCCUPIED_FILTER_OPTIONS,
            officeTypeOptions: OFFICE_TYPE_OPTIONS,
            roomTypeOptions: ROOM_TYPE_OPTIONS,
            yesNoBlankOptions: YES_NO_BLANK_OPTIONS,
            yesNoOptions: YES_NO_OPTIONS,
            showFormModal: false,
            editingId: null,
            saving: false,
            form: emptyForm(),
            tableHeaders: [
                "Select",
                "#",
                "Office Type",
                "Room",
                "Floor",
                "Occupied",
                "Stations",
                "Room Type",
                "Toilet",
                "Meeting",
                "Waiting",
                "Assistance",
                "Actions",
            ],
        };
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        permissions() {
            return this.$store.getters.permissions;
        },
        canCreate() {
            return this.hasPermission("facility-academic-offices-create");
        },
        canUpdate() {
            return this.hasPermission("facility-academic-offices-update");
        },
        canDelete() {
            return this.hasPermission("facility-academic-offices-delete");
        },
        canManage() {
            return this.canUpdate || this.canDelete;
        },
        paginatorInfoWithItems() {
            if (!this.paginatorInfo) return null;
            var pi = this.paginatorInfo;
            var firstItem =
                pi.firstItem || (pi.currentPage - 1) * pi.perPage + 1;
            var lastItem =
                pi.lastItem || Math.min(pi.currentPage * pi.perPage, pi.total);
            return {
                currentPage: pi.currentPage,
                lastPage: pi.lastPage,
                perPage: pi.perPage,
                total: pi.total,
                hasMorePages: pi.hasMorePages,
                firstItem: firstItem,
                lastItem: lastItem,
            };
        },
        bulkProgressPercent() {
            if (!this.bulkProgressTotal) return 0;
            return Math.round((this.bulkProgressCurrent / this.bulkProgressTotal) * 100);
        },
        allVisibleSelected() {
            if (!this.items || this.items.length === 0) return false;
            for (var i = 0; i < this.items.length; i++) {
                var id = String(this.items[i].id);
                if (this.selectedIds.indexOf(id) === -1) return false;
            }
            return true;
        },
    },
    created() {
        this.loadList();
    },
    methods: {
        hasPermission(name) {
            if (this.role === "superadmin") return true;
            return this.permissions && this.permissions.includes(name);
        },
        formatBooleanDisplay(value) {
            if (value === true) return "Yes";
            if (value === false) return "No";
            return "—";
        },
        formatStationsCount(value) {
            if (value === null || value === undefined || value === "") return "—";
            return String(value);
        },
        filterIsOccupiedValue() {
            if (this.filters.is_occupied === "yes") return true;
            if (this.filters.is_occupied === "no") return false;
            return null;
        },
        listQueryVariables(page, first) {
            return {
                first: first,
                page: page,
                entity: this.entity,
                campus: this.campus,
                office_type: this.filters.office_type || null,
                floor: this.filters.floor || null,
                room_type: this.filters.room_type || null,
                is_occupied: this.filterIsOccupiedValue(),
                search: this.search || null,
            };
        },
        onFilterChange() {
            this.page = 1;
            this.loadList();
        },
        rowNumber(index) {
            if (!this.paginatorInfo) return index + 1;
            var first = this.paginatorInfo.firstItem || 1;
            return first + index;
        },
        toggleSelectAll() {
            if (!this.canDelete) return;

            var ids = this.items.map(function (item) {
                return String(item.id);
            });
            if (!ids.length) return;

            if (this.allVisibleSelected) {
                this.selectedIds = this.selectedIds.filter(function (id) {
                    return ids.indexOf(id) === -1;
                });
            } else {
                var merged = this.selectedIds.concat(ids);
                var unique = [];
                for (var i = 0; i < merged.length; i++) {
                    if (unique.indexOf(merged[i]) === -1) unique.push(merged[i]);
                }
                this.selectedIds = unique;
            }
        },
        confirmBulkDelete() {
            var self = this;
            if (!this.$swal) return;

            if (!this.selectedIds || this.selectedIds.length === 0) return;

            this.$swal.fire({
                title: "Delete selected academic offices?",
                html:
                    "<div class='text-left text-sm text-gray-600'>" +
                    "<div><strong>Campus:</strong> " +
                    this.campus +
                    "</div>" +
                    "<div><strong>Records:</strong> " +
                    this.selectedIds.length +
                    "</div>" +
                    "</div>",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#dc2626",
                cancelButtonColor: "#6b7280",
                confirmButtonText: "Yes, delete",
            }).then(function (result) {
                if (result && result.isConfirmed) {
                    self.deleteSelected();
                }
            });
        },
        onSearch(value) {
            this.search = value || "";
            this.page = 1;
            this.loadList();
        },
        setPage(page) {
            this.page = page;
            this.loadList();
        },
        toastAlert(options) {
            if (!this.$swal) return Promise.resolve({});
            var merged = Object.assign({ timer: 2000 }, options);
            if (
                (merged.icon === "error" || merged.icon === "warning") &&
                (options.timer === undefined || options.timer === null)
            ) {
                merged.timer = 3500;
            }
            var self = this;
            var Toast = this.$swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timerProgressBar: true,
                didOpen: function (toast) {
                    toast.addEventListener("mouseenter", self.$swal.stopTimer);
                    toast.addEventListener("mouseleave", self.$swal.resumeTimer);
                },
            });
            return Toast.fire(merged);
        },
        loadList() {
            var self = this;
            this.loading = true;
            this.selectedIds = [];
            this.$apollo
                .query({
                    query: LIST_QUERY,
                    variables: this.listQueryVariables(this.page, this.perPage),
                    fetchPolicy: "network-only",
                })
                .then(function (result) {
                    var data = result && result.data ? result.data : null;
                    var payload =
                        data && data.facilityAcademicOffices
                            ? data.facilityAcademicOffices
                            : null;
                    if (payload && !payload.error) {
                        self.items = payload.data || [];
                        self.paginatorInfo = payload.paginatorInfo || null;
                    } else {
                        self.items = [];
                        self.paginatorInfo = null;
                        self.selectedIds = [];
                        self.toastAlert({
                            icon: "error",
                            title:
                                (payload && payload.message) ||
                                "Failed to load academic offices",
                        });
                    }
                })
                .catch(function (err) {
                    self.items = [];
                    self.paginatorInfo = null;
                    self.selectedIds = [];
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Failed to load academic offices",
                    });
                })
                .finally(function () {
                    self.loading = false;
                });
        },
        openCreate() {
            this.editingId = null;
            this.form = emptyForm();
            this.showFormModal = true;
        },
        openEdit(item) {
            this.editingId = item.id;
            this.form = {
                office_type: item.office_type || "",
                room: item.room || "",
                floor: item.floor || "",
                is_occupied: boolToFormYesNo(item.is_occupied),
                stations_count:
                    item.stations_count !== null && item.stations_count !== undefined
                        ? String(item.stations_count)
                        : "",
                room_type: item.room_type || "",
                has_toilet: boolToFormYesNoRequired(item.has_toilet),
                has_meeting: boolToFormYesNoRequired(item.has_meeting),
                has_waiting: boolToFormYesNoRequired(item.has_waiting),
                has_assistance_office: boolToFormYesNoRequired(item.has_assistance_office),
                remarks: item.remarks || "",
            };
            this.showFormModal = true;
        },
        closeForm() {
            this.showFormModal = false;
            this.editingId = null;
            this.form = emptyForm();
        },
        buildInput() {
            var stationsResult = parseStationsCountValue(this.form.stations_count);
            return {
                entity: this.entity,
                campus: this.campus,
                office_type: this.form.office_type || null,
                room: this.form.room || null,
                floor: this.form.floor || null,
                is_occupied: formYesNoToBool(this.form.is_occupied),
                stations_count: stationsResult.value,
                room_type: this.form.room_type || null,
                has_toilet: formYesNoToBool(this.form.has_toilet),
                has_meeting: formYesNoToBool(this.form.has_meeting),
                has_waiting: formYesNoToBool(this.form.has_waiting),
                has_assistance_office: formYesNoToBool(this.form.has_assistance_office),
                remarks: this.form.remarks || null,
            };
        },
        saveForm() {
            var self = this;
            if (!this.form.office_type || !String(this.form.office_type).trim()) {
                this.toastAlert({ icon: "warning", title: "Office type is required" });
                return;
            }
            if (!this.form.room || !String(this.form.room).trim()) {
                this.toastAlert({ icon: "warning", title: "Room is required" });
                return;
            }

            var stationsResult = parseStationsCountValue(this.form.stations_count);
            if (stationsResult.error) {
                this.toastAlert({ icon: "warning", title: stationsResult.error });
                return;
            }

            this.saving = true;
            var input = this.buildInput();
            var request;

            if (this.editingId) {
                request = this.$apollo.mutate({
                    mutation: UPDATE_MUTATION,
                    variables: { id: this.editingId, input: input },
                });
            } else {
                request = this.$apollo.mutate({
                    mutation: CREATE_MUTATION,
                    variables: { input: input },
                });
            }

            request
                .then(function (res) {
                    var data = res && res.data ? res.data : null;
                    var payload = self.editingId
                        ? data && data.updateFacilityAcademicOffice
                        : data && data.createFacilityAcademicOffice;

                    if (payload && !payload.error) {
                        self.toastAlert({
                            icon: "success",
                            title: payload.message || "Saved successfully",
                        });
                        self.closeForm();
                        self.loadList();
                        self.$emit("changed");
                    } else {
                        self.toastAlert({
                            icon: "error",
                            title:
                                (payload && payload.message) || "Save failed",
                        });
                    }
                })
                .catch(function (err) {
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Save failed",
                    });
                })
                .finally(function () {
                    self.saving = false;
                });
        },
        confirmDelete(item) {
            var self = this;
            if (!this.$swal) return;

            this.$swal
                .fire({
                    title: "Delete this office?",
                    html:
                        "<div class='text-left text-sm text-gray-600'>" +
                        "<div><strong>Office Type:</strong> " +
                        (item.office_type || "—") +
                        "</div>" +
                        "<div><strong>Room:</strong> " +
                        (item.room || "—") +
                        "</div>" +
                        "<div><strong>Floor:</strong> " +
                        (item.floor || "—") +
                        "</div>" +
                        "</div>",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#dc2626",
                    cancelButtonColor: "#6b7280",
                    confirmButtonText: "Yes, delete",
                })
                .then(function (result) {
                    if (result && result.isConfirmed) {
                        self.deleteItem(item);
                    }
                });
        },
        openBulkUploadModal() {
            this.showBulkUploadModal = true;
            this.bulkStep = "select";
            this.bulkDragOver = false;
            this.bulkFileName = "";
            this.bulkValidRows = [];
            this.bulkValidationErrors = [];
            this.bulkUploading = false;
            this.bulkProgressCurrent = 0;
            this.bulkProgressTotal = 0;
            this.bulkCurrentName = "";
            this.bulkSuccessCount = 0;
            this.bulkFailCount = 0;
            this.bulkUploadErrors = [];
        },
        closeBulkUploadModal() {
            this.showBulkUploadModal = false;
            this.bulkStep = "select";
            this.bulkDragOver = false;
            this.bulkFileName = "";
            this.bulkValidRows = [];
            this.bulkValidationErrors = [];
            this.bulkUploading = false;
            this.bulkProgressCurrent = 0;
            this.bulkProgressTotal = 0;
            this.bulkCurrentName = "";
            this.bulkSuccessCount = 0;
            this.bulkFailCount = 0;
            this.bulkUploadErrors = [];

            if (this.$refs && this.$refs.bulkFileInput) {
                this.$refs.bulkFileInput.value = null;
            }
        },
        resetBulkSelection() {
            this.bulkStep = "select";
            this.bulkDragOver = false;
            this.bulkFileName = "";
            this.bulkValidRows = [];
            this.bulkValidationErrors = [];
            this.bulkUploading = false;
            this.bulkProgressCurrent = 0;
            this.bulkProgressTotal = 0;
            this.bulkCurrentName = "";
            this.bulkSuccessCount = 0;
            this.bulkFailCount = 0;
            this.bulkUploadErrors = [];

            if (this.$refs && this.$refs.bulkFileInput) {
                this.$refs.bulkFileInput.value = null;
            }
        },
        triggerBulkFilePicker() {
            if (this.$refs && this.$refs.bulkFileInput) {
                this.$refs.bulkFileInput.click();
            }
        },
        downloadBulkTemplate() {
            try {
                var link = document.createElement("a");
                link.href = IT_BULK_TEMPLATE_URL;
                link.download = IT_BULK_TEMPLATE_FILENAME;
                link.target = "_blank";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } catch (e) {
                this.toastAlert({ icon: "error", title: "Failed to download template" });
            }
        },
        onBulkFileDrop(event) {
            this.bulkDragOver = false;
            if (!event || !event.dataTransfer || !event.dataTransfer.files) return;
            var files = event.dataTransfer.files;
            if (!files.length) return;

            var file = files[0];
            if (file) {
                this.parseBulkXlsxFile(file);
            }
        },
        onBulkFileSelected(event) {
            var input = event && event.target ? event.target : null;
            if (!input || !input.files || !input.files.length) return;
            var file = input.files[0];
            if (file) {
                this.parseBulkXlsxFile(file);
            }
        },
        parseBulkXlsxFile(file) {
            var self = this;

            this.bulkUploading = false;
            this.bulkStep = "review";
            this.bulkFileName = file && file.name ? file.name : "Uploaded file";
            this.bulkValidRows = [];
            this.bulkValidationErrors = [];
            this.bulkUploadErrors = [];

            var reader = new FileReader();
            reader.onload = function (e) {
                try {
                    var data = e && e.target ? e.target.result : null;
                    if (!data) {
                        self.bulkValidationErrors = [
                            { row: 0, message: "Could not read Excel file." },
                        ];
                        return;
                    }

                    var workbook = xlsx.read(data, { type: "array" });
                    var sheetName = workbook.SheetNames && workbook.SheetNames.length ? workbook.SheetNames[0] : null;
                    if (!sheetName) {
                        self.bulkValidationErrors = [
                            { row: 0, message: "Excel sheet not found." },
                        ];
                        return;
                    }

                    var sheet = workbook.Sheets[sheetName];
                    var aoa = xlsx.utils.sheet_to_json(sheet, { header: 1, defval: "" });
                    var parsed = self.validateAndBuildBulkRows(aoa);

                    self.bulkValidRows = parsed.validRows;
                    self.bulkValidationErrors = parsed.errors;
                } catch (err) {
                    self.bulkValidationErrors = [
                        { row: 0, message: (err && err.message) ? err.message : "Failed to parse Excel file." },
                    ];
                }
            };

            reader.onerror = function () {
                self.bulkValidationErrors = [
                    { row: 0, message: "Failed to read Excel file." },
                ];
            };

            reader.readAsArrayBuffer(file);
        },
        validateAndBuildBulkRows(aoa) {
            var errors = [];
            var validRows = [];

            if (!aoa || !aoa.length) {
                return {
                    validRows: [],
                    errors: [{ row: 0, message: "The Excel file is empty." }],
                };
            }

            var headerRow = aoa[0] || [];
            var headerIndex = {};
            for (var i = 0; i < headerRow.length; i++) {
                var hn = normalizeHeader(headerRow[i]);
                if (hn) {
                    headerIndex[hn] = i;
                }
            }

            var idxRoom = resolveColumnIndex(headerIndex, "ROOM", ["ROOM#", "ROOM NO", "TITLE"]);
            if (idxRoom === undefined) {
                errors.push({
                    row: 1,
                    message: "Missing required column: ROOM. Please use the official template.",
                });
                return { validRows: [], errors: errors };
            }

            var idxOfficeType = resolveColumnIndex(headerIndex, "OFFICE TYPE", ["TYPE"]);
            var idxFloor = resolveColumnIndex(headerIndex, "FLOOR", ["LOCATION"]);
            var idxOccupied = resolveColumnIndex(headerIndex, "OCCUPIED", [
                "OCCUPATION",
                "OCCUPATION (YES/NO)",
                "IS OCCUPIED",
            ]);
            var idxStations = resolveColumnIndex(headerIndex, "STATIONS", [
                "NO. OFFICES / STATION",
                "NO OFFICES / STATION",
                "STATIONS COUNT",
                "NO. OFFICES",
                "OFFICES",
            ]);
            var idxRoomType = resolveColumnIndex(headerIndex, "ROOM TYPE", []);
            var idxToilet = resolveColumnIndex(headerIndex, "TOILET", []);
            var idxMeeting = resolveColumnIndex(headerIndex, "MEETING", []);
            var idxWaiting = resolveColumnIndex(headerIndex, "WAITING", []);
            var idxAssistance = resolveColumnIndex(headerIndex, "ASSISTANCE OFFICE", ["ASSISTANCE"]);
            var idxRemarks = resolveColumnIndex(headerIndex, "REMARKS", ["REMARK", "NOTES", "NOTE"]);

            for (var rowIndex = 1; rowIndex < aoa.length; rowIndex++) {
                var excelRow = rowIndex + 1;
                var row = aoa[rowIndex] || [];

                var rawOfficeType = cellAt(row, idxOfficeType);
                var rawRoom = cellAt(row, idxRoom);
                var rawFloor = cellAt(row, idxFloor);
                var rawOccupied = cellAt(row, idxOccupied);
                var rawStations = cellAt(row, idxStations);
                var rawRoomType = cellAt(row, idxRoomType);
                var rawToilet = cellAt(row, idxToilet);
                var rawMeeting = cellAt(row, idxMeeting);
                var rawWaiting = cellAt(row, idxWaiting);
                var rawAssistance = cellAt(row, idxAssistance);
                var rawRemarks = cellAt(row, idxRemarks);

                if (
                    rawOfficeType === "" &&
                    rawRoom === "" &&
                    rawFloor === "" &&
                    rawOccupied === "" &&
                    rawStations === "" &&
                    rawRoomType === "" &&
                    rawToilet === "" &&
                    rawMeeting === "" &&
                    rawWaiting === "" &&
                    rawAssistance === "" &&
                    rawRemarks === ""
                ) {
                    continue;
                }

                if (!rawRoom) {
                    errors.push({ row: excelRow, message: "Room is required." });
                    continue;
                }

                var occupiedResult = parseYesNoValue(rawOccupied, "Occupied");
                if (occupiedResult.error) {
                    errors.push({ row: excelRow, message: occupiedResult.error });
                    continue;
                }

                var stationsResult = parseStationsCountValue(rawStations);
                if (stationsResult.error) {
                    errors.push({ row: excelRow, message: stationsResult.error });
                    continue;
                }

                var toiletResult = parseYesNoValue(rawToilet, "Toilet");
                if (toiletResult.error) {
                    errors.push({ row: excelRow, message: toiletResult.error });
                    continue;
                }

                var meetingResult = parseYesNoValue(rawMeeting, "Meeting");
                if (meetingResult.error) {
                    errors.push({ row: excelRow, message: meetingResult.error });
                    continue;
                }

                var waitingResult = parseYesNoValue(rawWaiting, "Waiting");
                if (waitingResult.error) {
                    errors.push({ row: excelRow, message: waitingResult.error });
                    continue;
                }

                var assistanceResult = parseYesNoValue(rawAssistance, "Assistance Office");
                if (assistanceResult.error) {
                    errors.push({ row: excelRow, message: assistanceResult.error });
                    continue;
                }

                validRows.push({
                    excelRow: excelRow,
                    office_type: rawOfficeType !== "" ? rawOfficeType : null,
                    room: rawRoom,
                    floor: rawFloor !== "" ? rawFloor : null,
                    is_occupied: occupiedResult.value,
                    stations_count: stationsResult.value,
                    room_type: rawRoomType !== "" ? rawRoomType : null,
                    has_toilet: toiletResult.value,
                    has_meeting: meetingResult.value,
                    has_waiting: waitingResult.value,
                    has_assistance_office: assistanceResult.value,
                    remarks: rawRemarks !== "" ? rawRemarks : null,
                });
            }

            if (!validRows.length && !errors.length) {
                errors.push({ row: 0, message: "No valid rows found. Please add data rows in the template." });
            }

            return { validRows: validRows, errors: errors };
        },
        async startBulkUpload() {
            if (!this.bulkValidRows.length) {
                this.toastAlert({ icon: "warning", title: "No rows to upload." });
                return;
            }

            this.bulkUploading = true;
            this.bulkStep = "uploading";
            this.bulkProgressTotal = this.bulkValidRows.length;
            this.bulkProgressCurrent = 0;
            this.bulkCurrentName = "";
            this.bulkSuccessCount = 0;
            this.bulkFailCount = 0;
            this.bulkUploadErrors = [];

            try {
                var chunkSize = 200;
                var total = this.bulkValidRows.length;
                var offset = 0;

                while (offset < total) {
                    var chunk = this.bulkValidRows.slice(
                        offset,
                        offset + chunkSize
                    );

                    if (chunk && chunk.length) {
                        var first = chunk[0];
                        this.bulkCurrentName = first.room || "—";
                    } else {
                        this.bulkCurrentName = "";
                    }

                    var input = {
                        entity: this.entity,
                        campus: this.campus,
                        rows: chunk.map(function (r) {
                            return {
                                row: r.excelRow,
                                office_type: r.office_type,
                                room: r.room,
                                floor: r.floor,
                                is_occupied: r.is_occupied,
                                stations_count: r.stations_count,
                                room_type: r.room_type,
                                has_toilet: r.has_toilet,
                                has_meeting: r.has_meeting,
                                has_waiting: r.has_waiting,
                                has_assistance_office: r.has_assistance_office,
                                remarks: r.remarks,
                            };
                        }),
                    };

                    try {
                        var res = await this.$apollo.mutate({
                            mutation: CREATE_BULK_MUTATION,
                            variables: { input: input },
                        });

                        var root =
                            res && res.data
                                ? res.data.createFacilityAcademicOfficesBulk
                                : null;
                        var payloadData = root && root.data ? root.data : null;

                        if (payloadData) {
                            this.bulkSuccessCount += payloadData.success_count || 0;
                            this.bulkFailCount += payloadData.fail_count || 0;

                            if (payloadData.results && payloadData.results.length) {
                                for (var j = 0; j < payloadData.results.length; j++) {
                                    var rr = payloadData.results[j];
                                    if (rr && rr.error) {
                                        this.bulkUploadErrors.push({
                                            row: rr.row,
                                            message:
                                                rr.message || "Failed to create office",
                                        });
                                    }
                                }
                            }
                        } else {
                            var emptyPayloadMessage =
                                (root && root.message)
                                    ? root.message
                                    : "Bulk upload failed";
                            for (var k = 0; k < chunk.length; k++) {
                                this.bulkFailCount += 1;
                                this.bulkUploadErrors.push({
                                    row:
                                        (chunk[k] && chunk[k].excelRow)
                                            ? chunk[k].excelRow
                                            : offset + k + 1,
                                    message: emptyPayloadMessage,
                                });
                            }
                        }
                    } catch (err) {
                        var catchMessage =
                            (err && err.message) ? err.message : "Bulk upload failed";
                        for (var m = 0; m < chunk.length; m++) {
                            this.bulkFailCount += 1;
                            this.bulkUploadErrors.push({
                                row:
                                    (chunk[m] && chunk[m].excelRow)
                                        ? chunk[m].excelRow
                                        : offset + m + 1,
                                message: catchMessage,
                            });
                        }
                    }

                    offset = offset + (chunk ? chunk.length : 0);
                    this.bulkProgressCurrent = offset;
                }
            } finally {
                this.bulkUploading = false;
                this.bulkStep = "done";
                this.loadList();
                this.$emit("changed");
            }
        },
        exportFileName() {
            return "ADPOLY_" + this.campus + "_Academic_Offices";
        },
        mapExportRow(item) {
            return {
                "Office Type": item.office_type || "",
                Room: item.room || "",
                Floor: item.floor || "",
                Occupied: this.formatBooleanDisplay(item.is_occupied),
                Stations:
                    item.stations_count !== null && item.stations_count !== undefined
                        ? item.stations_count
                        : "",
                "Room Type": item.room_type || "",
                Toilet: this.formatBooleanDisplay(item.has_toilet),
                Meeting: this.formatBooleanDisplay(item.has_meeting),
                Waiting: this.formatBooleanDisplay(item.has_waiting),
                "Assistance Office": this.formatBooleanDisplay(item.has_assistance_office),
                Remarks: item.remarks || "",
            };
        },
        exportSheetName() {
            return "Academic Offices";
        },
        _getExportQuery: function () {
            return { query: LIST_QUERY, root: "facilityAcademicOffices" };
        },
        _snapshotExportVariables: function () {
            var entity = this.entity;
            var campus = this.campus;
            var search = this.search || null;
            var officeType = this.filters.office_type || null;
            var floor = this.filters.floor || null;
            var roomType = this.filters.room_type || null;
            var isOccupied = this.filterIsOccupiedValue();
            return function (page, limit) {
                return {
                    first: limit,
                    page: page,
                    entity: entity,
                    campus: campus,
                    office_type: officeType,
                    floor: floor,
                    room_type: roomType,
                    is_occupied: isOccupied,
                    search: search,
                };
            };
        },
        async fetchForExporting(pageNo, limitSize) {
            try {
                var response = await this.$apollo.query({
                    query: LIST_QUERY,
                    variables: this.listQueryVariables(pageNo, limitSize),
                    fetchPolicy: "network-only",
                });

                return response.data && response.data.facilityAcademicOffices;
            } catch (error) {
                return {
                    error: true,
                    message: (error && error.message) || "Failed to fetch data",
                };
            }
        },
        deleteItem(item) {
            var self = this;
            this.$apollo
                .mutate({
                    mutation: DELETE_MUTATION,
                    variables: { id: item.id },
                })
                .then(function (res) {
                    var data = res && res.data ? res.data : null;
                    var payload =
                        data && data.deleteFacilityAcademicOffice
                            ? data.deleteFacilityAcademicOffice
                            : null;
                    if (payload && !payload.error) {
                        self.toastAlert({
                            icon: "success",
                            title: payload.message || "Deleted",
                        });
                        if (self.items.length === 1 && self.page > 1) {
                            self.page = self.page - 1;
                        }
                        self.loadList();
                        self.$emit("changed");
                    } else {
                        self.toastAlert({
                            icon: "error",
                            title:
                                (payload && payload.message) || "Delete failed",
                        });
                    }
                })
                .catch(function (err) {
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Delete failed",
                    });
                });
        },
        deleteSelected() {
            var self = this;
            if (!this.selectedIds || this.selectedIds.length === 0) return;
            if (this.deletingSelected) return;

            this.deletingSelected = true;

            var deletingAllOnPage =
                this.items &&
                this.items.length === this.selectedIds.length;
            if (deletingAllOnPage && this.page > 1) {
                this.page = this.page - 1;
            }

            this.$apollo
                .mutate({
                    mutation: DELETE_BULK_MUTATION,
                    variables: {
                        input: {
                            entity: this.entity,
                            campus: this.campus,
                            ids: this.selectedIds,
                        },
                    },
                })
                .then(function (res) {
                    var data = res && res.data ? res.data : null;
                    var payload =
                        data && data.deleteFacilityAcademicOfficesBulk
                            ? data.deleteFacilityAcademicOfficesBulk
                            : null;

                    if (payload && !payload.error) {
                        self.toastAlert({
                            icon: "success",
                            title:
                                payload.message ||
                                "Selected academic offices deleted successfully",
                        });
                        self.selectedIds = [];
                        self.loadList();
                        self.$emit("changed");
                    } else {
                        self.toastAlert({
                            icon: "error",
                            title:
                                (payload && payload.message) ||
                                "Bulk delete failed",
                        });
                        self.selectedIds = [];
                        self.loadList();
                    }
                })
                .catch(function (err) {
                    self.toastAlert({
                        icon: "error",
                        title: (err && err.message) || "Bulk delete failed",
                    });
                    self.selectedIds = [];
                    self.loadList();
                })
                .finally(function () {
                    self.deletingSelected = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.ds-wrap {
    background: linear-gradient(180deg, #f8fafc 0%, #ffffff 40%);
}

.ds-toolbar {
    padding: 1.25rem 1rem 1rem;
}

@media (min-width: 640px) {
    .ds-toolbar {
        padding-left: 2rem;
        padding-right: 2rem;
    }
}

.ds-toolbar__row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.75rem 1rem;
}

.ds-toolbar__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
}

.ds-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.75rem;
    flex: 1 1 auto;
}

.ds-filter__label {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
    margin-bottom: 0.25rem;
}

.ds-select,
.ds-input {
    width: 100%;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    padding: 0.45rem 0.75rem;
    font-size: 0.875rem;
    background: #ffffff;
    color: #111827;
}

.ds-filter-input {
    min-width: 10rem;
    height: 2.25rem;
}

.ds-textarea {
    resize: vertical;
    min-height: 5rem;
}

.ds-select {
    min-width: 8.5rem;
    height: 2.25rem;
}

.ds-select:focus,
.ds-input:focus {
    outline: none;
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.15);
}

.ds-search {
    flex: 1 1 14rem;
    min-width: 12rem;
    max-width: 22rem;
}

.ds-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    padding: 0.55rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    transition: background 0.15s ease, border-color 0.15s ease;
}

.ds-btn--primary {
    background: #0f766e;
    color: #ffffff;
    border: 1px solid #0f766e;
}

.ds-btn--primary:hover {
    background: #115e59;
}

.ds-export-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    white-space: nowrap;
    border-radius: 0.75rem;
    border: 1px solid rgba(16, 185, 129, 0.25);
    background: linear-gradient(to bottom, #ffffff, rgba(16, 185, 129, 0.08));
    padding: 0.625rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #052e1f;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    transition: border-color 150ms ease, background 150ms ease, box-shadow 150ms ease;
}

.ds-export-btn:hover {
    border-color: rgba(16, 185, 129, 0.45);
    background: linear-gradient(to bottom, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.14));
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
}

.ds-export-btn:disabled {
    pointer-events: none;
    opacity: 0.6;
}

.ds-export-btn__icon {
    font-size: 1.05rem;
    color: #059669;
    filter: drop-shadow(0 1px 0 rgba(0, 0, 0, 0.06));
}

.ds-btn--ghost {
    background: #ffffff;
    color: #374151;
    border: 1px solid #d1d5db;
}

.ds-btn--ghost:hover {
    background: #f9fafb;
}

.ds-btn--danger {
    background: #dc2626;
    color: #ffffff;
    border: 1px solid #dc2626;
}

.ds-btn--danger:hover {
    background: #b91c1c;
}

.ds-btn--danger:disabled {
    pointer-events: none;
    opacity: 0.6;
}

.ds-checkbox {
    width: 1rem;
    height: 1rem;
    accent-color: #0f766e;
}

.ds-bulk {
    padding: 1rem 2rem;
}

.ds-bulk__banner {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    background: #ecfeff;
    border: 1px solid rgba(15, 118, 110, 0.18);
    border-radius: 0.75rem;
    padding: 1rem 1.25rem;
    margin-bottom: 1rem;
}

.ds-bulk__banner-icon {
    font-size: 1.25rem;
    color: #0f766e;
    line-height: 1;
}

.ds-bulk__banner-title {
    font-size: 1rem;
    font-weight: 800;
    color: #0f766e;
    margin: 0;
}

.ds-bulk__banner-hint {
    margin: 0.35rem 0 0;
    font-size: 0.85rem;
    color: #4b5563;
    line-height: 1.35;
}

.ds-bulk__section {
    padding: 0 0.25rem 0.5rem;
}

.ds-bulk__dropzone {
    border: 2px dashed rgba(15, 118, 110, 0.35);
    background: #f8fafc;
    border-radius: 0.85rem;
    padding: 1.5rem 1rem;
    text-align: center;
    cursor: pointer;
}

.ds-bulk__dropzone--active {
    border-color: rgba(15, 118, 110, 0.8);
    background: #ecfeff;
}

.ds-bulk__dropzone-icon {
    font-size: 1.5rem;
    color: #0f766e;
}

.ds-bulk__dropzone-title {
    margin: 0.75rem 0 0.25rem;
    font-size: 0.95rem;
    font-weight: 700;
    color: #0f766e;
}

.ds-bulk__dropzone-sub {
    margin: 0;
    font-size: 0.82rem;
    color: #6b7280;
}

.ds-bulk__tips {
    margin-top: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.ds-bulk__link-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    border-radius: 0.75rem;
    border: 1px solid rgba(59, 130, 246, 0.25);
    background: rgba(59, 130, 246, 0.06);
    padding: 0.6rem 1rem;
    font-size: 0.85rem;
    font-weight: 700;
    color: #1d4ed8;
    cursor: pointer;
}

.ds-bulk__link-btn:hover {
    background: rgba(59, 130, 246, 0.12);
    border-color: rgba(59, 130, 246, 0.45);
}

.ds-bulk__tips-text {
    font-size: 0.85rem;
    color: #4b5563;
    line-height: 1.4;
}

.ds-bulk__file-card {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    border-radius: 0.9rem;
    padding: 0.85rem 1rem;
    margin-bottom: 1rem;
}

.ds-bulk__file-icon {
    color: #0f766e;
    font-size: 1.1rem;
}

.ds-bulk__file-name {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 800;
    color: #111827;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ds-bulk__file-meta {
    margin: 0.25rem 0 0;
    font-size: 0.82rem;
    color: #6b7280;
}

.ds-bulk__errors {
    margin-top: 0.75rem;
    border: 1px solid rgba(239, 68, 68, 0.2);
    background: rgba(254, 242, 242, 0.7);
    border-radius: 0.9rem;
    padding: 1rem;
}

.ds-bulk__errors-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 800;
    color: #b91c1c;
    margin-bottom: 0.75rem;
}

.ds-bulk__errors-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.ds-bulk__error-item {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: baseline;
    color: #7f1d1d;
    font-size: 0.85rem;
}

.ds-bulk__error-row {
    font-weight: 800;
}

.ds-bulk__ready {
    margin-top: 1rem;
    display: flex;
    gap: 0.85rem;
    align-items: flex-start;
    background: rgba(16, 185, 129, 0.08);
    border: 1px solid rgba(16, 185, 129, 0.22);
    border-radius: 0.9rem;
    padding: 0.9rem 1rem;
}

.ds-bulk__ready-icon {
    margin-top: 0.1rem;
    color: #047857;
    font-size: 1.25rem;
}

.ds-bulk__ready-title {
    margin: 0;
    font-weight: 900;
    color: #047857;
    line-height: 1.25;
}

.ds-bulk__ready-sub {
    margin: 0.35rem 0 0;
    color: #065f46;
    font-size: 0.85rem;
}

.ds-bulk__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1rem;
}

.ds-bulk__footer--single {
    justify-content: flex-end;
}

.ds-bulk__progress-card {
    border: 1px solid #e5e7eb;
    background: #ffffff;
    border-radius: 0.9rem;
    padding: 1rem;
}

.ds-bulk__progress-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
}

.ds-bulk__progress-title {
    font-weight: 800;
    color: #111827;
}

.ds-bulk__progress-percent {
    font-weight: 900;
    color: #0f766e;
}

.ds-bulk__progress-track {
    height: 10px;
    background: #f3f4f6;
    border-radius: 9999px;
    overflow: hidden;
}

.ds-bulk__progress-fill {
    height: 10px;
    background: #0f766e;
    border-radius: 9999px;
}

.ds-bulk__progress-sub {
    margin: 0.75rem 0 0;
    color: #6b7280;
    font-size: 0.85rem;
}

.ds-bulk__progress-name {
    margin: 0.5rem 0 0;
    color: #374151;
    font-size: 0.85rem;
    font-weight: 700;
}

.ds-bulk__summary {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 0.75rem;
    margin-bottom: 1rem;
}

.ds-bulk__summary-stat {
    border: 1px solid #e5e7eb;
    background: #ffffff;
    border-radius: 0.9rem;
    padding: 0.85rem 1rem;
}

.ds-bulk__summary-stat--ok {
    border-color: rgba(16, 185, 129, 0.3);
    background: rgba(16, 185, 129, 0.08);
}

.ds-bulk__summary-stat--fail {
    border-color: rgba(239, 68, 68, 0.3);
    background: rgba(239, 68, 68, 0.08);
}

.ds-bulk__summary-stat--ok .ds-bulk__summary-value {
    color: #047857;
}

.ds-bulk__summary-stat--fail .ds-bulk__summary-value {
    color: #b91c1c;
}

.ds-bulk__summary-value {
    font-size: 1.6rem;
    font-weight: 900;
    margin: 0;
    color: #111827;
}

.ds-bulk__summary-label {
    margin: 0.25rem 0 0;
    color: #6b7280;
    font-size: 0.85rem;
    font-weight: 700;
}

.ds-table-shell {
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.ds-th {
    padding: 0.75rem 1rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #ffffff;
    background: #0f766e;
    white-space: nowrap;
}

.ds-th:first-child {
    border-top-left-radius: 0.7rem;
}

.ds-th:last-child {
    border-top-right-radius: 0.7rem;
}

.ds-row:nth-child(even) {
    background: #f0fdfa;
}

.ds-row:hover {
    background: #ccfbf1;
}

.ds-td {
    padding: 0.7rem 1rem;
    color: #1f2937;
    border-top: 1px solid #e5e7eb;
    vertical-align: middle;
}

.ds-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
    background: #f3f4f6;
    margin-left: 0.35rem;
}

.ds-icon-btn:hover {
    background: #e0e7ff;
}

.ds-icon-btn--danger:hover {
    background: #fee2e2;
}

.ds-empty-icon {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
}

.ds-form__header {
    padding: 1.25rem 1.5rem 0.75rem;
    border-bottom: 1px solid #f3f4f6;
}

.ds-form__body {
    padding: 1.25rem 1.5rem;
}

.ds-form__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem 1.5rem 1.25rem;
    border-top: 1px solid #f3f4f6;
}

.ds-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.35rem;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
